/**
 * useGetCompetitionItems — the competitions inside an umbrella, by id.
 *
 * NOT keyed on a host. That is the whole point.
 *
 * The only way to resolve these used to be `useGetHubCompetitions(slug)`, which
 * hits `/hubs/slug/:slug/competitions`. The Drop page had the same need and no
 * hook of its own, so it passed a Drop slug to the Hub one — 404 when no Hub
 * shared the slug, and somebody else's competitions when one did.
 *
 * Challenges, quests, missions and objectives get PLACED on surfaces; they are
 * not owned by a surface type. Keying the fetch to a competition id rather than
 * to a Hub or a Drop means a new surface needs no new hook, no new route and
 * no branch in the components that render them — it passes the id it already
 * holds. Both Hub and Drop already carry `competitionId` on their models.
 *
 * Types come from `@devour/client`, not from a hand-written mirror. The sibling
 * Hub hook declares its own `GetHubCompetitionsResponse` interface and casts
 * the client response onto it with `as unknown as`; that is the pattern the
 * repo's CI diff-gate blocks, and copying it here would have added a new
 * instance of it. If a field looks missing, the swagger schema is what's wrong.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import {
    Token,
    CompetitionsApi,
    type SurfaceCompetitionResponse,
} from "@devour/client";

export type { SurfaceCompetitionResponse };

/**
 * Filters accepted by the endpoint.
 *
 * Declared here rather than imported from the Hub hook so this file does not
 * depend on the surface it replaced.
 */
export interface CompetitionItemsFilter {
    type?: string;
    activeOnly?: boolean;
    limit?: number;
    offset?: number;
}

export const COMPETITION_ITEMS_QUERY_KEY = "competition-items" as const;

export const competitionItemsKeys = {
    all: [COMPETITION_ITEMS_QUERY_KEY] as const,
    byId: (competitionId: string) => [COMPETITION_ITEMS_QUERY_KEY, competitionId] as const,
    withFilter: (
        competitionId: string,
        filter: CompetitionItemsFilter,
        userId?: string,
    ) => [
        COMPETITION_ITEMS_QUERY_KEY,
        competitionId,
        filter.type ?? "all",
        filter.activeOnly ?? false,
        filter.limit ?? 20,
        filter.offset ?? 0,
        userId ?? "anon",
    ] as const,
};

/*
 * Return type INFERRED from the generated client rather than annotated with
 * `GetCompetitionItemsResponse`.
 *
 * Same generated type either way — but naming it requires the models barrel to
 * re-export it, and the barrel is regenerated in CI. Inferring keeps this file
 * correct regardless of what the barrel happens to name, without falling back
 * to a hand-written mirror of the response shape.
 */
async function fetchCompetitionItems(
    competitionId: string,
    filter: CompetitionItemsFilter,
    fullToken?: Token,
) {
    return new CompetitionsApi(getConfig(fullToken)).getCompetitionsIdItems({
        id: competitionId,
        type: filter.type as never,
        activeOnly: filter.activeOnly as never,
        limit: filter.limit,
        offset: filter.offset,
    });
}

/**
 * Competitions inside an umbrella.
 *
 * @param competitionId - the umbrella id, from whatever surface is rendering
 * @param filter - optional type / activeOnly / pagination
 */
export function useGetCompetitionItems(
    competitionId?: string,
    filter: CompetitionItemsFilter = {},
) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: competitionItemsKeys.withFilter(competitionId ?? "", filter, fullToken?.id),
        queryFn: () => fetchCompetitionItems(competitionId!, filter, fullToken),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        /*
         * A surface with no competition attached is the ordinary case, not an
         * error — it simply makes no request, and the section renders nothing.
         */
        enabled: Boolean(competitionId),
        placeholderData: keepPreviousData,
    });
}
